"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MarketDetail } from "@/lib/api";
import {
  OutcomeRowList,
  type OutcomeBuySide,
} from "@/components/markets/OutcomeRowList";

/**
 * EPIC-053 STORY-A — client island that owns the selected-outcome
 * state on the multi-outcome market detail page.
 *
 * Why an island, not state on the page itself: `page.tsx` is a Next
 * 16 Server Component (it `await api.getMarket(...)` at the top).
 * Server components can't hold useState. The shell wraps the
 * (client) `<OutcomeRowList>` + handles the cross-island handshake
 * with the right-rail `<MultiOutcomeTradePanel>`:
 *
 *   - On mount, picks the highest-volume outcome and broadcasts
 *     it via `granbay.multiOutcome.select` so the trade panel
 *     renders the trade form for that outcome unconditionally.
 *   - On row select (anywhere except a Buy button), broadcasts the
 *     new outcome id with side="yes" so the panel mode stays
 *     "buy" by default.
 *   - On Buy YES / Buy NO click, broadcasts with the chosen side
 *     so the panel pre-selects buy/sell mode. NO is modeled by the
 *     matcher as "buy NO shares" — the panel maps side="no" to
 *     mode="sell" internally (per Polymarket convention).
 *   - On mobile (<lg), scrolls the trade panel into view so the
 *     user sees what they triggered.
 *   - Listens for `granbay.multiOutcome.panelSelect` so the row
 *     highlight stays in sync when the user clicks a row inside
 *     the panel directly (legacy fallback path).
 *
 * STORY-F empty/copy:
 *   - Section header reads "Mercados disponíveis" (pt) /
 *     "Available markets" (en) — Polymarket "Outcomes" pattern.
 *   - When zero outcomes have non-zero volume, a subtle hint above
 *     the list reads "Click an outcome to trade" so new users on
 *     a freshly-opened market know the rows are interactive.
 */
interface Props {
  market: MarketDetail;
  viewerLocale: string;
}

export function OutcomeRowListShell({ market, viewerLocale }: Props) {
  const { t } = useTranslation();

  // Default = highest-volume outcome. Matches Polymarket's "lead
  // with the favorite" pattern. Memoized on the outcomes array so
  // the default doesn't churn between renders.
  const defaultOutcomeId = useMemo(() => {
    if (market.outcomes.length === 0) return null;
    const sorted = [...market.outcomes].sort(
      (a, b) => Number(b.volumeCents) - Number(a.volumeCents),
    );
    return sorted[0].id;
  }, [market.outcomes]);

  const [selectedOutcomeId, setSelectedOutcomeId] = useState<string | null>(
    defaultOutcomeId,
  );

  // Whether any outcome has non-zero volume. Drives the
  // "Click an outcome to trade" empty-state hint per STORY F. The
  // hint stays visible until at least one outcome's volume turns
  // positive (typically the first trade of the day).
  const hasAnyVolume = useMemo(
    () => market.outcomes.some((o) => Number(o.volumeCents) > 0),
    [market.outcomes],
  );

  /* eslint-disable react-hooks/set-state-in-effect --
   * Default-selection broadcast + panel-side mirror are both
   * "sync external state into React" effects — the disable
   * follows the codebase pattern (see TradePanel.tsx +
   * LivePriceChart.tsx). */

  // Broadcast the default selection on first mount (+ any time the
  // computed default changes — e.g. an outcome's volume flips on a
  // subsequent server-revalidate) so the trade panel doesn't render
  // the "Pick an outcome" placeholder when the user lands on the
  // page. `side: 'yes'` matches the row component's default-click
  // semantics.
  useEffect(() => {
    if (!defaultOutcomeId) return;
    setSelectedOutcomeId(defaultOutcomeId);
    window.dispatchEvent(
      new CustomEvent("granbay.multiOutcome.select", {
        detail: { outcomeId: defaultOutcomeId, side: "yes" },
      }),
    );
  }, [defaultOutcomeId]);

  // Mirror panel-side row clicks back into the shell so the row
  // highlight tracks the panel's "currently expanded outcome".
  useEffect(() => {
    const onPanelChange = (e: Event) => {
      const ce = e as CustomEvent<{ outcomeId: string | null }>;
      if (typeof ce.detail?.outcomeId !== "undefined") {
        setSelectedOutcomeId(ce.detail.outcomeId);
      }
    };
    window.addEventListener(
      "granbay.multiOutcome.panelSelect",
      onPanelChange as EventListener,
    );
    return () =>
      window.removeEventListener(
        "granbay.multiOutcome.panelSelect",
        onPanelChange as EventListener,
      );
  }, []);

  // 2026-05-15 mobile polish — listen for the bottom-bar's "Change ↕"
  // tap and scroll this section into view so the user can pick a
  // different outcome without manually scrolling. No-op on desktop
  // because the bottom bar is `md:hidden` and never dispatches there.
  useEffect(() => {
    const onScroll = () => {
      const el = document.getElementById("outcome-row-list");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener(
      "granbay.multiOutcome.scrollToList",
      onScroll as EventListener,
    );
    return () =>
      window.removeEventListener(
        "granbay.multiOutcome.scrollToList",
        onScroll as EventListener,
      );
  }, []);

  /* eslint-enable react-hooks/set-state-in-effect */

  const broadcast = (outcomeId: string, side: OutcomeBuySide) => {
    window.dispatchEvent(
      new CustomEvent("granbay.multiOutcome.select", {
        detail: { outcomeId, side },
      }),
    );
    // Mobile scroll-into-view: panel sits below the rows on <lg
    // widths. Scroll smoothly so the user sees what they triggered
    // and focus the amount input so they can type a size right away
    // on desktop (mobile doesn't auto-focus to avoid raising the
    // keyboard before the user's seen the prefilled price).
    if (typeof window === "undefined") return;
    const panel = document.getElementById("multi-outcome-trade-panel");
    if (panel) {
      if (window.innerWidth < 1024) {
        panel.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        const amount = panel.querySelector<HTMLInputElement>(
          'input[type="number"][placeholder="0"]',
        );
        amount?.focus();
      }
    }
  };

  const handleSelect = (outcomeId: string) => {
    setSelectedOutcomeId(outcomeId);
    broadcast(outcomeId, "yes");
  };

  const handleBuy = (outcomeId: string, side: OutcomeBuySide) => {
    setSelectedOutcomeId(outcomeId);
    broadcast(outcomeId, side);
  };

  return (
    <section id="outcome-row-list" className="mb-4 scroll-mt-16">
      <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
        {t("outcomeRowList.sectionHeading")}
      </h2>
      {!hasAnyVolume && (
        <p className="mb-2 text-[11px] text-[var(--color-text3)]">
          {t("outcomeRowList.emptyHint")}
        </p>
      )}
      <OutcomeRowList
        outcomes={market.outcomes}
        viewerLocale={viewerLocale}
        selectedOutcomeId={selectedOutcomeId}
        onSelect={handleSelect}
        onBuy={handleBuy}
      />
    </section>
  );
}
